import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, Upload, FileText, Trash2, Plus, MapPin } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { InspectionStatusBadge, formatNlDate } from "@/components/Badges";
import { plural } from "@/lib/plural";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/PageConsole";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InlineError, InlineEmpty } from "@/components/ListState";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_LABEL = {
  verlopen: "Verlopen", te_controleren: "Te controleren", binnenkort: "Binnenkort",
  geldig: "Geldig", onbekend: "Onbekend",
};

// Round 28 §6: the register. Joey opens it and has the overview straight away —
// what is due where, whether a certificate is on file, and open bevindingen.
export default function Keuringen() {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [types, setTypes] = useState([]);
  const [fLoc, setFLoc] = useState("all");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [ins, loc, ty] = await Promise.all([
        api.get("/inspections"), api.get("/locations"),
        api.get("/inspection-types?include_inactive=true"),
      ]);
      setRows(ins.data); setLocations(loc.data); setTypes(ty.data);
    } catch (e) { setError(getErrorMessage(e, "Kan keuringen niet laden.")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const locName = useMemo(() => {
    const m = {};
    for (const l of locations) m[l.location_id] = l.name;
    return m;
  }, [locations]);

  // One normalisation used by BOTH the grouping and the filter, so "Voertuigen"
  // and "Geen locatie" can never fall into different buckets.
  const groupKey = (r) => r.effective_location_id || (r.is_vehicle ? "__vehicles" : "__none");

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) =>
      (fLoc === "all" || groupKey(r) === fLoc) &&
      (fType === "all" || r.type_id === fType) &&
      (fStatus === "all" || r.status === fStatus)
    );
  }, [rows, fLoc, fType, fStatus]);

  // Group per location, vehicles/geen-locatie last.
  const groups = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const key = groupKey(r);
      (g[key] = g[key] || []).push(r);
    }
    const label = (key) =>
      key === "__vehicles" ? "Voertuigen" : key === "__none" ? "Geen locatie" : (locName[key] || key);
    return Object.keys(g)
      .sort((a, b) => {
        const rank = (k) => (k === "__vehicles" ? 2 : k === "__none" ? 3 : 1);
        return rank(a) - rank(b) || label(a).localeCompare(label(b));
      })
      .map((key) => ({ key, label: label(key), rows: g[key] }));
  }, [filtered, locName]);

  const urgent = rows ? rows.filter((r) => r.status === "verlopen" || r.status === "te_controleren").length : 0;
  const binnenkort = rows ? rows.filter((r) => r.status === "binnenkort").length : 0;
  const facts = rows ? [
    plural(urgent, "verlopen of te controleren", "verlopen of te controleren"),
    plural(binnenkort, "binnenkort", "binnenkort"),
  ] : [];

  return (
    <Page console={{ variant: "view", title: "Keuringen", facts }}>
      {error ? <InlineError testid="keuringen-load-error" message={error} onRetry={load} /> :
        !rows ? <p className="text-body text-muted-foreground">Laden…</p> :
          rows.length === 0 ? (
            <InlineEmpty testid="keuringen-empty" title="Nog geen keuringen in het register"
              purpose="Leg per locatie of machine vast welke keuringen er zijn, wanneer ze zijn gedaan en bewaar het certificaat. Dat doe je op een locatie of op een machine." />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3" data-testid="keuringen-filters">
                <div className="min-w-[160px]">
                  <Select value={fLoc} onValueChange={setFLoc}>
                    <SelectTrigger data-testid="keuringen-filter-locatie"><SelectValue placeholder="Locatie" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle locaties</SelectItem>
                      {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                      <SelectItem value="__vehicles">Voertuigen</SelectItem>
                      <SelectItem value="__none">Geen locatie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[160px]">
                  <Select value={fType} onValueChange={setFType}>
                    <SelectTrigger data-testid="keuringen-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle types</SelectItem>
                      {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.naam}{t.active === false ? " (inactief)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[160px]">
                  <Select value={fStatus} onValueChange={setFStatus}>
                    <SelectTrigger data-testid="keuringen-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle statussen</SelectItem>
                      {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {groups.length === 0 ? (
                <InlineEmpty testid="keuringen-filter-empty" title="Niets binnen dit filter"
                  purpose="Pas het filter aan om andere keuringen te zien." />
              ) : groups.map((grp) => (
                <div key={grp.key} data-testid={`keuringen-group-${grp.key}`}>
                  <h2 className="mb-2 flex items-center gap-1.5 text-body font-semibold text-foreground">
                    <MapPin size={16} /> {grp.label}
                  </h2>
                  <Card className="hidden sm:block">
                    <Table data-testid={`keuringen-table-${grp.key}`}>
                      <TableHeader><TableRow>
                        <TableHead>Keuring</TableHead><TableHead>Plaats</TableHead>
                        <TableHead>Volgende</TableHead><TableHead>Status</TableHead>
                        <TableHead>Certificaat</TableHead><TableHead>Bevindingen</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {grp.rows.map((r) => (
                          <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetailId(r.id)}>
                            <TableCell className="text-foreground">
                              <button
                                type="button"
                                data-testid={`keuring-row-${r.id}`}
                                className="text-left font-medium text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                                onClick={(e) => { e.stopPropagation(); setDetailId(r.id); }}
                              >
                                {r.name}{r.license_plate ? ` (${r.license_plate})` : ""}
                              </button>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{r.asset_code || r.location_name || "—"}</TableCell>
                            <TableCell className="text-muted-foreground">{r.next_due ? formatNlDate(r.next_due) : "—"}</TableCell>
                            <TableCell><InspectionStatusBadge status={r.status} /></TableCell>
                            <TableCell className="text-muted-foreground">{r.heeft_certificaat ? "op dossier" : "—"}</TableCell>
                            <TableCell className="text-muted-foreground">{r.open_bevindingen > 0 ? plural(r.open_bevindingen, "open", "open") : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                  <div className="space-y-2 sm:hidden" data-testid={`keuringen-cardlist-${grp.key}`}>
                    {grp.rows.map((r) => (
                      <Card key={r.id} data-testid={`keuring-card-${r.id}`} role="button" tabIndex={0} className="cursor-pointer p-4 transition-colors hover:bg-accent" onClick={() => setDetailId(r.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailId(r.id); } }}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-body font-medium text-foreground">{r.name}{r.license_plate ? ` (${r.license_plate})` : ""}</span>
                          <InspectionStatusBadge status={r.status} />
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-data text-muted-foreground">
                          <span>{r.asset_code || r.location_name || "—"}</span>
                          {r.next_due && <span>volgende {formatNlDate(r.next_due)}</span>}
                          <span>{r.heeft_certificaat ? "certificaat op dossier" : "geen certificaat"}</span>
                          {r.open_bevindingen > 0 && <span>{plural(r.open_bevindingen, "open bevinding", "open bevindingen")}</span>}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

      <KeuringDetail
        id={detailId}
        locations={locations}
        writable={writable}
        onClose={() => setDetailId(null)}
        onChanged={load}
      />
    </Page>
  );
}

// ---------------------------------------------------------------- detail dialog
const KeuringDetail = ({ id, locations, writable, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [uForm, setUForm] = useState(null); // {datum, uitgevoerd_door, opmerking}
  const [bText, setBText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileFor = useRef({});

  const load = useCallback(async () => {
    if (!id) { setData(null); return; }
    try { const { data } = await api.get(`/inspections/${id}`); setData(data); }
    catch (e) { toast.error(getErrorMessage(e, "Kan keuring niet laden.")); onClose(); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const addUitvoering = async () => {
    if (!uForm?.datum) { toast.error("Kies een datum."); return; }
    setBusy(true);
    try {
      await api.post(`/inspections/${id}/uitvoeringen`, uForm);
      toast.success("Uitvoering vastgelegd");
      setUForm(null);
      await refresh();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setBusy(false); }
  };

  const uploadCert = async (uid, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Alleen PDF — een certificaat is een PDF."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/inspections/${id}/uitvoeringen/${uid}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
      });
      toast.success(`Certificaat toegevoegd: ${file.name}`);
      await refresh();
    } catch (e) { toast.error(getErrorMessage(e, "Uploaden mislukt.")); }
    finally { setBusy(false); }
  };

  const removeCert = async (uid, key) => {
    if (!window.confirm("Certificaat verwijderen?")) return;
    setBusy(true);
    try { await api.delete(`/inspections/${id}/uitvoeringen/${uid}/documents`, { params: { key } }); await refresh(); }
    catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
    finally { setBusy(false); }
  };

  const openDoc = async (d) => {
    try {
      const resp = await api.get("/storage/file", { params: { key: d.key }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: d.mime || "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(getErrorMessage(e, "Openen mislukt.")); }
  };

  const addBevinding = async () => {
    if (!bText.trim()) { toast.error("Geef een op-/aanmerking."); return; }
    setBusy(true);
    try { await api.post(`/inspections/${id}/bevindingen`, { tekst: bText }); setBText(""); await refresh(); }
    catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setBusy(false); }
  };

  const toggleBevinding = async (b) => {
    setBusy(true);
    try {
      await api.put(`/inspections/${id}/bevindingen/${b.id}`, { status: b.status === "open" ? "afgehandeld" : "open" });
      await refresh();
    } catch (e) { toast.error(getErrorMessage(e, "Wijzigen mislukt.")); }
    finally { setBusy(false); }
  };

  const move = async (newLoc) => {
    setBusy(true);
    try { await api.put(`/inspections/${id}`, { location_id: newLoc }); toast.success("Keuring verplaatst"); await refresh(); }
    catch (e) { toast.error(getErrorMessage(e, "Verplaatsen mislukt.")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent srDescription="Bekijk uitvoeringen, certificaten en bevindingen van deze keuring." data-testid="keuring-detail">
        {!data ? <p className="text-body text-muted-foreground">Laden…</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck size={18} /> {data.name}{data.license_plate ? ` (${data.license_plate})` : ""}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-data text-muted-foreground">
                <InspectionStatusBadge status={data.status} />
                <span>{data.asset_code || data.location_name || "geen plaats"}</span>
                {data.next_due && <span>volgende {formatNlDate(data.next_due)}</span>}
              </div>

              {/* Move to another location (location-bound only). */}
              {writable && !data.asset_id && (
                <div className="space-y-1.5">
                  <Label>Locatie</Label>
                  <Select value={data.location_id || ""} onValueChange={move}>
                    <SelectTrigger data-testid="keuring-move-locatie"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Uitvoeringen */}
              <section data-testid="keuring-uitvoeringen">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-body font-semibold text-foreground">Uitvoeringen</h3>
                  {writable && !uForm && (
                    <Button size="sm" variant="outline" data-testid="uitvoering-add-button" onClick={() => setUForm({ datum: "", uitgevoerd_door: "", opmerking: "" })}>
                      <Plus size={16} className="mr-1.5" /> Uitvoering toevoegen
                    </Button>
                  )}
                </div>
                {uForm && (
                  <Card className="mb-2 space-y-2 p-3" data-testid="uitvoering-form">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label>Datum</Label><Input type="date" data-testid="uitvoering-datum" value={uForm.datum} onChange={(e) => setUForm((f) => ({ ...f, datum: e.target.value }))} /></div>
                      <div className="space-y-1"><Label>Uitgevoerd door</Label><Input data-testid="uitvoering-door" value={uForm.uitgevoerd_door} onChange={(e) => setUForm((f) => ({ ...f, uitgevoerd_door: e.target.value }))} placeholder="bijv. keuringsbedrijf" /></div>
                    </div>
                    <div className="space-y-1"><Label>Opmerking</Label><Textarea data-testid="uitvoering-opmerking" value={uForm.opmerking} onChange={(e) => setUForm((f) => ({ ...f, opmerking: e.target.value }))} /></div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setUForm(null)}>Annuleren</Button>
                      <Button size="sm" data-testid="uitvoering-save" onClick={addUitvoering} disabled={busy}>Opslaan</Button>
                    </div>
                  </Card>
                )}
                {data.uitvoeringen.length === 0 ? (
                  <p className="text-data text-muted-foreground" data-testid="keuring-uitvoeringen-empty">Nog niet uitgevoerd — status blijft onbekend tot er een uitvoering met datum is.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.uitvoeringen.map((u) => (
                      <li key={u.id} className="py-2" data-testid={`uitvoering-${u.id}`}>
                        <div className="text-body text-foreground">{formatNlDate(u.datum)}{u.uitgevoerd_door ? ` · ${u.uitgevoerd_door}` : ""}</div>
                        {u.opmerking && <div className="text-data text-muted-foreground">{u.opmerking}</div>}
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {(u.documenten || []).map((d) => (
                            <span key={d.key} className="flex items-center gap-1 text-data">
                              <button type="button" className="flex items-center gap-1 text-foreground hover:underline" data-testid={`certificaat-open-${d.key}`} onClick={() => openDoc(d)}>
                                <FileText size={14} /> {d.name}
                              </button>
                              {writable && (
                                <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`certificaat-delete-${d.key}`} onClick={() => removeCert(u.id, d.key)}><Trash2 size={14} /></Button>
                              )}
                            </span>
                          ))}
                          {writable && (
                            <>
                              <input
                                type="file" accept="application/pdf" className="hidden"
                                ref={(el) => { fileFor.current[u.id] = el; }}
                                data-testid={`certificaat-file-${u.id}`}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadCert(u.id, f); }}
                              />
                              <Button size="sm" variant="outline" data-testid={`certificaat-upload-${u.id}`} onClick={() => fileFor.current[u.id]?.click()} disabled={busy}>
                                <Upload size={14} className="mr-1.5" /> Certificaat (PDF)
                              </Button>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Bevindingen */}
              <section data-testid="keuring-bevindingen">
                <h3 className="mb-2 text-body font-semibold text-foreground">Op- en aanmerkingen</h3>
                {data.bevindingen.length === 0 ? (
                  <p className="text-data text-muted-foreground" data-testid="keuring-bevindingen-empty">Geen op-/aanmerkingen.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.bevindingen.map((b) => (
                      <li key={b.id} className="flex items-start justify-between gap-3 py-2" data-testid={`bevinding-${b.id}`}>
                        <div className="min-w-0">
                          <div className={`text-body ${b.status === "afgehandeld" ? "text-muted-foreground line-through" : "text-foreground"}`}>{b.tekst}</div>
                          <div className="text-data text-muted-foreground">{b.auteur_naam || ""}{b.datum ? ` · ${formatNlDate((b.datum || "").slice(0, 10))}` : ""}</div>
                        </div>
                        {writable && (
                          <Button size="sm" variant="outline" data-testid={`bevinding-toggle-${b.id}`} onClick={() => toggleBevinding(b)} disabled={busy}>
                            {b.status === "open" ? "Afhandelen" : "Heropenen"}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {writable && (
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="bevinding-input" value={bText} onChange={(e) => setBText(e.target.value)} placeholder="Nieuwe op-/aanmerking" />
                    <Button data-testid="bevinding-add-button" onClick={addBevinding} disabled={busy}>Toevoegen</Button>
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
